// src/pages/parts/HostList.js
import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api'; 
import '../style/PageStyle/Hosts.css';

const HostList = ({ onOrganisationSelect, selectedOrganisation }) => {
  const [Organisations, setOrganisations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    loadOrganisations();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds([]); // Clear selection on filter change
  }, [searchTerm, selectedType, itemsPerPage]);

  const loadOrganisations = async () => {
    try {
      setIsLoading(true);
      const OrganisationData = await apiService.getOrganisations();
      
      // --- Sort Alphabetically by Name ---
      const sortedData = OrganisationData.sort((a, b) => {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });

      setOrganisations(sortedData);
    } catch (error) {
      console.error('Failed to load Organisations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const uniqueTypes = [...new Set(Organisations.map(org => org.type).filter(Boolean))].sort();

  // Filter Organisations based on search term AND selected type
  const filteredOrganisations = Organisations.filter(org => {
    const term = searchTerm.toLowerCase();
    
    const matchesSearch = 
      org.name.toLowerCase().includes(term) ||
      (org.custom_id && org.custom_id.toString().toLowerCase().includes(term)) || 
      (org.type && org.type.toLowerCase().includes(term)) || 
      (org.location && org.location.toLowerCase().includes(term));

    const matchesType = selectedType === '' || org.type === selectedType;

    return matchesSearch && matchesType;
  });

  // --- Pagination Logic ---
  const totalPages = Math.ceil(filteredOrganisations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentOrganisations = filteredOrganisations.slice(startIndex, startIndex + itemsPerPage);

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(prev => prev - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(prev => prev + 1);
  };

    const handleExport = async () => {
    try {
      setIsLoading(true);
      await apiService.exportOrganisations();
    } catch (error) {
      console.error('Failed to export organisations:', error);
      alert('Failed to export data.');
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle individual checkbox
  const handleSelectOne = (id, e) => {
    e.stopPropagation(); // Prevent row click event
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(itemId => itemId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  // Toggle "Select All" (for current page only)
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const currentIds = currentOrganisations.map(org => org.id);
      // Merge current page IDs with existing selection, avoiding duplicates
      const newSelection = [...new Set([...selectedIds, ...currentIds])];
      setSelectedIds(newSelection);
    } else {
      // Deselect items that are on the current page
      const currentIds = currentOrganisations.map(org => org.id);
      setSelectedIds(selectedIds.filter(id => !currentIds.includes(id)));
    }
  };

  // Check if all items on current page are selected
  const isAllSelected = currentOrganisations.length > 0 && 
    currentOrganisations.every(org => selectedIds.includes(org.id));

  // --- UPDATED: Bulk Delete Handler with Data Check ---
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;

    // 1. Find the actual objects for the selected IDs to check their counts
    const selectedItems = Organisations.filter(org => selectedIds.includes(org.id));

    // 2. Check if any selected item has users or documents
    const itemsWithData = selectedItems.filter(
      org => (org.userCount > 0 || org.documentCount > 0)
    );

    let confirmMessage = '';

    if (itemsWithData.length > 0) {
      // WARNING MESSAGE: Data exists
      confirmMessage = `⚠️ WARNING: ${itemsWithData.length} of the selected organisations contain Users or Documents.\n\n`;
      confirmMessage += `Deleting them will PERMANENTLY REMOVE all associated users and documents.\n\n`;
      confirmMessage += `Are you sure you want to delete these ${selectedIds.length} organisations?`;
    } else {
      // STANDARD MESSAGE: No data
      confirmMessage = `Are you sure you want to delete ${selectedIds.length} organisation(s)? This action cannot be undone.`;
    }

    // 3. Ask for confirmation
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setIsLoading(true);
      
      // Use the bulk API endpoint if you implemented it, otherwise loop
      if (apiService.deleteOrganisationsBulk) {
         await apiService.deleteOrganisationsBulk(selectedIds);
      } else {
         // Fallback to looping if bulk endpoint isn't ready
         await Promise.all(selectedIds.map(id => apiService.deleteOrganisation(id)));
      }
      
      // Refresh list
      await loadOrganisations();
      setSelectedIds([]); // Clear selection
      alert('Organisations deleted successfully.');
    } catch (error) {
      console.error('Failed to delete organisations:', error);
      alert('Failed to delete some organisations.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) return <div className="loading">Loading Organisations...</div>;

  return (
    <div className="host-list">
      <div className="host-list-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h2>Organisations List</h2>
            <span style={{
              backgroundColor: '#e2e8f0',
              color: '#475569',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '0.85rem',
              fontWeight: '600'
            }}>
              {filteredOrganisations.length}
            </span>
          </div>
          
          {/* ---  EXPORT BUTTON --- */}
          <button 
            onClick={handleExport}
            style={{
              backgroundColor: '#10b981', // Green color for Excel
              color: 'white',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
            Export Excel
          </button>
        </div>
        
        {/* --- Delete Button (Visible only when items selected) --- */}
        {selectedIds.length > 0 && (
          <button 
            onClick={handleBulkDelete}
            style={{
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Delete Selected ({selectedIds.length})
          </button>
        )}
      </div>


      <div className="host-list-controls" style={{ display: 'flex', gap: '10px', marginBottom: '15px', alignItems: 'center' }}>
        {/* Search Input */}
        <div className="search-control" style={{ flex: 1 }}>
          <input
            type="text"
            placeholder="Search by Name, ID, Type, or Location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
          />
        </div>

        {/* Items Per Page Filter */}
        <div className="items-per-page-control" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ fontSize: '0.9rem', color: '#666' }}>Show</span>
            <select 
                value={itemsPerPage} 
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', cursor: 'pointer' }}
            >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
            </select>
            <span style={{ fontSize: '0.9rem', color: '#666' }}>lines</span>
        </div>

        {/* Type Filter Dropdown */}
        <div className="filter-control">
          <select 
            value={selectedType} 
            onChange={(e) => setSelectedType(e.target.value)}
            className="type-select"
            style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', minWidth: '150px' }}
          >
            <option value="">All Types</option>
            {uniqueTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="Organisations-table-container">
        <table className="Organisations-table">
          <thead>
            <tr>
              <th style={{ width: '40px', textAlign: 'center' }}>
                <input 
                  type="checkbox" 
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  style={{ cursor: 'pointer' }}
                />
              </th>
              <th style={{ width: '80px' }}>ID</th>
              <th>Organisation Name</th>
              <th>Type</th>
              <th>Rebate %</th>
              <th>Redistribution %</th>
              <th>Users</th>
              <th>Documents</th>
              <th>Contact</th>
            </tr>
          </thead>

          <tbody>
            {currentOrganisations.map(org => {
              const isSelected = selectedIds.includes(org.id);
              return (
                <tr 
                  key={org.id}
                  className={`Organisation-row ${
                    selectedOrganisation?.id === org.id ? 'selected' : ''
                  } ${isSelected ? 'row-checked' : ''}`}
                  onClick={() => onOrganisationSelect && onOrganisationSelect(org)}
                  style={{ cursor: 'pointer', backgroundColor: isSelected ? '#f0f9ff' : '' }}
                >
                  <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <input 
                      type="checkbox" 
                      checked={isSelected}
                      onChange={(e) => handleSelectOne(org.id, e)}
                      style={{ cursor: 'pointer' }}
                    />
                  </td>

                  <td>
                    <span style={{ fontWeight: '600', color: '#64748b', fontSize: '0.9rem' }}>
                      {org.custom_id || '-'}
                    </span>
                  </td>

                  <td>
                    <div className="org-name-cell">
                      <span className="org-name">{org.name}</span>
                      <span className="org-description">{org.description}</span>
                    </div>
                  </td>
                  
                  <td>
                    <span className="org-type-badge" style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '0.85rem',
                      backgroundColor: '#eef2ff',
                      color: '#4f46e5',
                      border: '1px solid #c7d2fe'
                    }}>
                      {org.type || 'N/A'}
                    </span>
                  </td>

                  <td>
                    <span style={{ fontWeight: '500', color: '#334155' }}>
                      {org.rebate ? `${org.rebate}%` : '-'}
                    </span>
                  </td>
                  
                  <td>
                    <span style={{ fontWeight: '500', color: '#334155' }}>
                      {org.redistribution ? `${org.redistribution}%` : '-'}
                    </span>
                  </td>

                  <td>
                    <span className="user-count" style={{ 
                        fontWeight: org.userCount > 0 ? 'bold' : 'normal',
                        color: org.userCount > 0 ? '#d97706' : 'inherit' 
                    }}>
                        {org.userCount || 0} users
                    </span>
                  </td>
                  
                  <td>
                    <span className="document-count" style={{ 
                        fontWeight: org.documentCount > 0 ? 'bold' : 'normal',
                        color: org.documentCount > 0 ? '#d97706' : 'inherit' 
                    }}>
                        {org.documentCount || 0} documents
                    </span>
                  </td>
                  
                  <td>
                    <span className="contact-email">{org.contactEmail || 'N/A'}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* --- Pagination Controls --- */}
      {filteredOrganisations.length > 0 ? (
        <div className="pagination-container">
          <button 
            className="pagination-btn" 
            onClick={handlePrevPage} 
            disabled={currentPage === 1}
          >
            &laquo; Previous
          </button>
          
          <span className="pagination-info">
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </span>
          
          <button 
            className="pagination-btn" 
            onClick={handleNextPage} 
            disabled={currentPage === totalPages}
          >
            Next &raquo;
          </button>
        </div>
      ) : (
        !isLoading && (
          <div className="no-Organisations">
            <p>No Organisations found matching your criteria.</p>
          </div>
        )
      )}
    </div>
  );
};

export default HostList;
